import { useTranslation } from '@rhino-project/core/i18n';
import {
  useBaseOwnerId,
  useFeature,
  useHasRoleOf
} from '@rhino-project/core/hooks';
import { useMemo } from 'react';
import { type NavItemProps } from '@rhino-project/ui-heroui';
import { useActiveSeasons, usePlanningSeasons } from '../../../hooks/ots';
import { type LinkProps } from '@tanstack/react-router';
import { useMode } from '../../../hooks/useMode';

type NavItem = NavItemProps & Pick<LinkProps, 'to' | 'params'>;
type NavActionItem = {
  title: string;
  icon: string;
  disabled?: boolean;
  onAction: () => void;
};
type NavSection = { title?: string; items: (NavItem | NavActionItem)[] };

function usePlanItems(
  owner: string,
  hasPlanningSeason: boolean,
  isAdmin: boolean,
  planningSeasons:
    { id: number | string; season: { name: string } }[] | undefined
): NavSection[] {
  const { t } = useTranslation();

  // Reports ride evaluations (either base grants it); Scout stays on recruiting,
  // which covers watching players who aren't yours yet.
  const recruitingEnabled = useFeature('recruiting');
  const evaluationsEnabled = useFeature('evaluations');
  const planningSeasonItems: NavItem[] =
    planningSeasons?.map((ts) => ({
      title: String(ts?.season?.name),
      icon: 'bi:calendar',
      to: '/$owner/planning/$team_season_id' as const,
      params: { owner, team_season_id: String(ts.id) }
    })) || [];

  // The projection reads the current roster against the classes being
  // recruited into, so a single planning season is already a real comparison —
  // now versus next year. It trails the season links because it summarises
  // them.
  const projectionItems: NavItem[] =
    planningSeasonItems.length > 0
      ? [
          {
            title: t('frontend.messages.projection'),
            icon: 'bi:grid-3x3',
            to: '/$owner/projection' as const,
            params: { owner }
          }
        ]
      : [];

  return [
    {
      items: [
        {
          title: t('frontend.messages.dashboard'),
          icon: 'bi:house',
          to: '/$owner/plan' as const,
          params: { owner },
          activeOptions: { exact: true }
        },
        {
          title: t('frontend.messages.players'),
          icon: 'bi:people',
          to: '/$owner/players' as const,
          params: { owner }
        },
        ...(evaluationsEnabled
          ? [
              {
                title: t('frontend.messages.reports'),
                icon: 'bi:pen',
                to: '/$owner/reports' as const,
                params: { owner }
              }
            ]
          : []),
        {
          title: t('frontend.messages.ghost_rosters'),
          icon: 'bi:list-check',
          disabled: !hasPlanningSeason,
          to: '/$owner/ghost' as const,
          params: { owner }
        },
        ...(recruitingEnabled
          ? [
              {
                title: t('frontend.messages.scout'),
                icon: 'bi:binoculars',
                disabled: !hasPlanningSeason || !isAdmin,
                to: '/$owner/scout' as const,
                params: { owner }
              }
            ]
          : []),
        {
          title: t('frontend.messages.database'),
          icon: 'bi:search',
          to: '/$owner/database' as const,
          params: { owner }
        }
      ]
    },
    {
      title: t('frontend.messages.seasons'),
      items: [...planningSeasonItems, ...projectionItems]
    }
  ];
}

function useSeasonItems(
  owner: string,
  hasActiveSeason: boolean,
  activeSeasonId: string | number | undefined
): NavSection[] {
  const { t } = useTranslation();

  const tsId = String(activeSeasonId ?? '');
  const seasonExtrasEnabled = useFeature('player_portal');

  const items: (NavItem | NavActionItem)[] = [
    {
      title: t('frontend.messages.dashboard'),
      icon: 'bi:house',
      to: '/$owner' as const,
      params: { owner },
      activeOptions: { exact: true }
    },
    {
      title: t('frontend.messages.roster'),
      icon: 'bi:person-lines-fill',
      disabled: !hasActiveSeason,
      to: '/$owner/active/$team_season_id' as const,
      params: { owner, team_season_id: tsId },
      activeOptions: { exact: true }
    },
    {
      title: t('frontend.messages.current_players'),
      icon: 'bi:people',
      disabled: !hasActiveSeason,
      to: '/$owner/active/$team_season_id/players' as const,
      params: { owner, team_season_id: tsId }
    },
    {
      title: t('frontend.messages.depth_chart'),
      icon: 'bi:diagram-3',
      disabled: !hasActiveSeason,
      to: '/$owner/active/$team_season_id/depth-chart' as const,
      params: { owner, team_season_id: tsId }
    },
    {
      title: t('frontend.messages.lineups'),
      icon: 'bi:list',
      disabled: !hasActiveSeason,
      to: '/$owner/active/$team_season_id/lineups' as const,
      params: { owner, team_season_id: tsId }
    }
  ];

  if (seasonExtrasEnabled) {
    items.push(
      {
        title: t('frontend.messages.prep_videos'),
        icon: 'bi:play-btn',
        disabled: !hasActiveSeason,
        to: '/$owner/active/$team_season_id/videos' as const,
        params: { owner, team_season_id: tsId },
        activeOptions: { exact: true }
      },
      {
        title: t('frontend.messages.team_videos'),
        icon: 'bi:collection-play',
        disabled: !hasActiveSeason,
        to: '/$owner/active/$team_season_id/videos/systems' as const,
        params: { owner, team_season_id: tsId }
      },
      {
        title: t('frontend.messages.practice_plans'),
        icon: 'bi:clipboard',
        disabled: !hasActiveSeason,
        to: '/$owner/active/$team_season_id/practice-plans' as const,
        params: { owner, team_season_id: tsId }
      },
      {
        title: t('frontend.messages.stats'),
        icon: 'bi:bar-chart',
        disabled: !hasActiveSeason,
        to: '/$owner/active/$team_season_id/stats' as const,
        params: { owner, team_season_id: tsId }
      },
      {
        title: t('frontend.messages.calendar'),
        icon: 'bi:calendar-event',
        disabled: !hasActiveSeason,
        to: '/$owner/active/$team_season_id/calendar' as const,
        params: { owner, team_season_id: tsId }
      },
      {
        title: t('frontend.messages.portal'),
        icon: 'bi:door-open',
        disabled: !hasActiveSeason,
        to: '/$owner/active/$team_season_id/portal' as const,
        params: { owner, team_season_id: tsId }
      }
    );
  }

  return [{ items }];
}

/**
 * A custom hook that manages the primary navigation structure based on the current app mode.
 *
 * Two modes:
 * - **Plan**: Future season planning & scouting (Players, Reports, Planning Seasons, Ghost, Scout, Database)
 * - **Season**: Current/active season management (Dashboard, Roster, Lineups, Videos, Practice Plans, Stats, Calendar)
 */
export function useNavigation() {
  const owner = String(useBaseOwnerId());
  const { resources: active_seasons } = useActiveSeasons();
  const { resources: planning_seasons } = usePlanningSeasons();
  const isAdmin = useHasRoleOf('admin');
  const { mode } = useMode();

  const hasActiveSeason = Boolean(
    active_seasons?.total && active_seasons?.total > 0
  );
  const hasPlanningSeason = Boolean(
    planning_seasons?.total && planning_seasons?.total > 0
  );

  const activeSeasonId = active_seasons?.results?.[0]?.id;

  const planSections = usePlanItems(
    owner,
    hasPlanningSeason,
    isAdmin,
    planning_seasons?.results as
      { id: number | string; season: { name: string } }[] | undefined
  );
  const seasonSections = useSeasonItems(owner, hasActiveSeason, activeSeasonId);

  const sections = useMemo(() => {
    switch (mode) {
      case 'plan':
        return planSections;
      case 'season':
      default:
        return seasonSections;
    }
  }, [mode, planSections, seasonSections]);

  return {
    sections,
    hasActiveSeason,
    hasPlanningSeason
  };
}
